import { RequestUserConfig } from '../common/types'
import { SearchUrlRequestBodyTransport } from '../transports/search-url-transport'
import { searchClient } from '../clients'
import { useRequestUserConfigContext } from './request'
import { useAsyncCallback } from './async'
import { useCallback, useRef, useEffect } from 'react'
import ListingSectionTransport from '../transports/common/listing-section-transport'
import { getTopListingsByCombinedViewCount, MAX_LISTING_VIEW_COUNTS } from '../utils/listing-view-count-utils'
import isEmpty from 'lodash/isEmpty'
import { useSessionEffect } from './session'

const _getSearchUrl = async (user: RequestUserConfig, filter: string, params: SearchUrlRequestBodyTransport) => {
    const response = await searchClient.getSearchUrl({
        user,
        filter,
        params,
    })

    return response.data.searchUrl
}

export const useSearchUrl = () => {
    const user = useRequestUserConfigContext()

    const { data: searchUrl, isLoading, error, callback } = useAsyncCallback<string>(_getSearchUrl)

    const getSearchUrl = useCallback(
        (filter: string, params: SearchUrlRequestBodyTransport) => callback(user, filter, params),
        [callback, user],
    )

    return {
        searchUrl,
        isLoading,
        error,
        getSearchUrl,
    }
}

const _getContinueYourSearchListings = async (user: RequestUserConfig, params: { tfwObjectIds: number[] }) => {
    const response = await searchClient.getContinueYourSearchListings({
        user,
        params,
    })

    return response.data
}

export const useContinueYourSearchListings = () => {
    const user = useRequestUserConfigContext()
    const {
        data: listings,
        isLoading,
        error,
        callback,
    } = useAsyncCallback<ListingSectionTransport>(_getContinueYourSearchListings)

    const hasDataToFetchRef = useRef(false)
    const requestedOrderRef = useRef<number[]>([])

    const getContinueYourSearchListings = useCallback(
        (params: { tfwObjectIds: number[] }) => callback(user, params),
        [callback, user],
    )

    const fetchContinueYourSearch = useCallback(async () => {
        if (!isEmpty(listings)) {
            return
        }

        const topListingIds = await getTopListingsByCombinedViewCount(MAX_LISTING_VIEW_COUNTS)
        const tfwObjectIds = topListingIds.map(id => Number.parseInt(id)).filter(id => !Number.isNaN(id))

        if (tfwObjectIds.length > 0) {
            requestedOrderRef.current = tfwObjectIds
            getContinueYourSearchListings({ tfwObjectIds })
        }
    }, [listings, getContinueYourSearchListings])

    // Check if there's data to fetch on mount
    useEffect(() => {
        const checkForData = async () => {
            const topListingIds = await getTopListingsByCombinedViewCount(MAX_LISTING_VIEW_COUNTS)
            const tfwObjectIds = topListingIds.map(id => Number.parseInt(id)).filter(id => !Number.isNaN(id))
            hasDataToFetchRef.current = tfwObjectIds.length > 0
        }

        checkForData()
    }, [])

    useSessionEffect(() => {
        fetchContinueYourSearch()
    }, [fetchContinueYourSearch])

    const sortedListings = listings
        ? {
              ...listings,
              objects: [...listings.objects].sort((a, b) => {
                  const listingIdA = Number(a.id)
                  const listingIdB = Number(b.id)
                  const indexA = requestedOrderRef.current.indexOf(listingIdA)
                  const indexB = requestedOrderRef.current.indexOf(listingIdB)

                  if (indexA === -1 && indexB === -1) {
                      return 0
                  }
                  if (indexA === -1) {
                      return 1
                  }
                  if (indexB === -1) {
                      return -1
                  }
                  return indexA - indexB
              }),
          }
        : listings

    const shouldShowLoading = isLoading || (hasDataToFetchRef.current && isEmpty(listings))

    return {
        listings: sortedListings,
        isLoading: shouldShowLoading,
        error,
    }
}
